import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { receivablesService } from "../services/dataService";
import { FirmStatement as FirmStatementDto, LedgerEntry, LedgerEntryKind, PaymentMethod } from "../types";
import { formatCurrency } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import LedgerEntryModal from "../components/LedgerEntryModal";
import "./Receivables.css";

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");

const KIND_LABEL: Record<LedgerEntryKind, string> = {
  [LedgerEntryKind.Invoice]: "Fatura",
  [LedgerEntryKind.Payment]: "Tahsilat",
  [LedgerEntryKind.DebitNote]: "Borç Dekontu",
  [LedgerEntryKind.CreditNote]: "Alacak Dekontu",
  [LedgerEntryKind.Opening]: "Devir",
  [LedgerEntryKind.Reversal]: "İptal (ters kayıt)",
};
const METHOD_LABEL: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: "Nakit",
  [PaymentMethod.BankTransfer]: "Havale/EFT",
  [PaymentMethod.Cheque]: "Çek/Senet",
  [PaymentMethod.CreditCard]: "Kredi Kartı",
  [PaymentMethod.Other]: "Diğer",
};

const FirmStatement: React.FC = () => {
  const { firmId = "" } = useParams();
  const { has } = usePermissions();
  const [data, setData] = useState<FirmStatementDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<{ entry?: LedgerEntry | null; kind?: LedgerEntryKind } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setData(await receivablesService.statement(firmId)); }
    catch (e: any) { setError(e?.response?.data?.message || (typeof e?.response?.data === "string" ? e.response.data : "Ekstre yüklenemedi.")); }
    finally { setLoading(false); }
  }, [firmId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (e: LedgerEntry) => {
    if (!window.confirm(`${KIND_LABEL[e.kind]} kaydı silinsin mi? ${formatCurrency(e.amount, e.currency)} — ${fmtDate(e.entryDate)}`)) return;
    setDeleting(e.firmLedgerEntryId);
    try { await receivablesService.remove(e.firmLedgerEntryId); await load(); }
    catch (err: any) { setError(err?.response?.data || "Silinemedi."); }
    finally { setDeleting(null); }
  };

  if (loading && !data) return <PageLoader message="Ekstre yükleniyor..." />;
  if (!data) return <div className="rc-page"><div className="ic-alert error">{error || "Firma bulunamadı."}</div></div>;

  const mainCurrency = data.balances[0]?.currency || "TRY";

  return (
    <div className="rc-page">
      <Link className="rc-crumb" to="/receivables">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
        Cari Hesaplar
      </Link>

      <div className="rc-firm-head">
        <div>
          <h1>{data.firmName}</h1>
          <div className="rc-firm-meta">
            {data.firmCode}{data.taxNumber ? ` · VKN ${data.taxNumber}` : ""} · Varsayılan vade {data.defaultTermDays} gün
          </div>
          <div className="rc-head-actions" style={{ marginTop: "0.875rem" }}>
            {has("receivables:create") && (
              <>
                <button className="rc-btn primary" onClick={() => setModal({ kind: LedgerEntryKind.Payment })}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                  Tahsilat Gir
                </button>
                <button className="rc-btn" onClick={() => setModal({ kind: LedgerEntryKind.DebitNote })}>Dekont / Devir</button>
              </>
            )}
            <button className="rc-btn" onClick={load} disabled={loading}>Yenile</button>
          </div>
        </div>

        <div className="rc-balances">
          {data.balances.length === 0 && (
            <div className="rc-balance"><div className="rc-balance-label">Bakiye</div><div className="rc-balance-value">—</div></div>
          )}
          {data.balances.map((b) => (
            <React.Fragment key={b.currency}>
              <div className="rc-balance">
                <div className="rc-balance-label">Bakiye · {b.currency}</div>
                <div className="rc-balance-value">{formatCurrency(b.balance, b.currency)}</div>
                <div className="rc-balance-sub">Borç {formatCurrency(b.debit, b.currency)} · Alacak {formatCurrency(b.credit, b.currency)}</div>
              </div>
              {b.overdue > 0 && (
                <div className="rc-balance overdue">
                  <div className="rc-balance-label">Vadesi Geçmiş · {b.currency}</div>
                  <div className="rc-balance-value">{formatCurrency(b.overdue, b.currency)}</div>
                  <div className="rc-balance-sub">Vadesi gelmemiş {formatCurrency(b.notDue, b.currency)}</div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {error && <div className="ic-alert error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {/* Açık kalemler */}
      <div className="rc-card">
        <div className="rc-card-head">
          <div><h2>Açık Kalemler</h2><p>Tahsilatlar en eski borca kapatılır (FIFO); kalanlar burada listelenir.</p></div>
          <span className="rc-chip neutral">{data.openItems.length} kalem</span>
        </div>
        <div className="rc-table-wrap">
          <table className="rc-table rt rt-stack">
            <thead>
              <tr>
                <th>Belge</th><th className="rt-p3">Tarih</th><th>Vade</th>
                <th className="num rt-p4">Tutar</th><th className="num">Kalan</th><th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {data.openItems.length === 0 ? (
                <tr><td colSpan={6}><div className="rc-empty"><strong>Açık kalem yok</strong>Tüm borçlar kapatılmış.</div></td></tr>
              ) : data.openItems.map((o) => (
                <tr key={o.firmLedgerEntryId}>
                  <td className="rt-main"><span className="strong">{o.referenceNo || "—"}</span>{o.description && <div className="muted" style={{ fontSize: "0.75rem" }}>{o.description}</div>}</td>
                  <td className="rt-p3" data-label="Tarih">{fmtDate(o.entryDate)}</td>
                  <td data-label="Vade">{fmtDate(o.dueDate)}</td>
                  <td className="num rt-p4" data-label="Tutar">{formatCurrency(o.amount, o.currency)}</td>
                  <td className="num strong" data-label="Kalan">{formatCurrency(o.remaining, o.currency)}</td>
                  <td data-label="Durum">
                    {o.daysOverdue > 0
                      ? <span className="rc-chip danger">{o.daysOverdue} gün gecikmiş</span>
                      : <span className="rc-chip ok">Vadesinde</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ekstre */}
      <div className="rc-card">
        <div className="rc-card-head">
          <div><h2>Hesap Ekstresi</h2><p>Tüm hareketler tarih sırasıyla; sistem kayıtları düzenlenemez.</p></div>
          <span className="rc-chip neutral">{data.entries.length} hareket</span>
        </div>
        <div className="rc-table-wrap">
          <table className="rc-table rt rt-stack">
            <thead>
              <tr>
                <th>Tarih</th><th>Hareket</th><th className="rt-p3">Açıklama</th>
                <th className="num">Borç</th><th className="num">Alacak</th>
                <th className="num rt-p2">Bakiye</th><th className="rt-p4">Kaydeden</th><th className="rt-actions" />
              </tr>
            </thead>
            <tbody>
              {data.entries.length === 0 ? (
                <tr><td colSpan={8}><div className="rc-empty"><strong>Henüz hareket yok</strong></div></td></tr>
              ) : [...data.entries].reverse().map((e) => (
                <tr key={e.firmLedgerEntryId}>
                  <td data-label="Tarih">{fmtDate(e.entryDate)}</td>
                  <td className="rt-main">
                    <span className={`rc-kind ${e.isDebit ? "debit" : "credit"}`}>
                      <i />{KIND_LABEL[e.kind]}
                      {e.kind === LedgerEntryKind.Payment && e.paymentMethod ? <span className="muted"> · {METHOD_LABEL[e.paymentMethod]}</span> : null}
                      {e.financialAccountName ? <span className="muted"> · {e.financialAccountName}</span> : null}
                      {e.isSystem && <span className="rc-sys">SİSTEM</span>}
                    </span>
                    {e.referenceNo && <div className="muted" style={{ fontSize: "0.75rem" }}>{e.referenceNo}</div>}
                  </td>
                  <td className="rt-p3 muted" data-label="Açıklama">{e.description || "—"}{e.dueDate && e.isDebit ? ` · vade ${fmtDate(e.dueDate)}` : ""}</td>
                  <td className="num" data-label="Borç">{e.isDebit ? formatCurrency(e.amount, e.currency) : ""}</td>
                  <td className="num" data-label="Alacak" style={{ color: e.isDebit ? undefined : "var(--rc-ok)" }}>{e.isDebit ? "" : formatCurrency(e.amount, e.currency)}</td>
                  <td className="num strong rt-p2" data-label="Bakiye">{formatCurrency(e.runningBalance, e.currency)}</td>
                  <td className="rt-p4 muted" data-label="Kaydeden">{e.createdBy || "—"}</td>
                  <td className="rt-actions">
                    {!e.isSystem && (
                      <div style={{ display: "flex", gap: "0.375rem", justifyContent: "flex-end" }}>
                        {has("receivables:update") && (
                          <button className="rc-btn sm" onClick={() => setModal({ entry: e })}>Düzenle</button>
                        )}
                        {has("receivables:delete") && (
                          <button className="rc-btn sm danger-ghost" disabled={deleting === e.firmLedgerEntryId} onClick={() => remove(e)}>Sil</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <LedgerEntryModal
          firmId={data.firmId}
          firmName={data.firmName}
          entry={modal.entry}
          initialKind={modal.kind}
          defaultCurrency={mainCurrency}
          defaultTermDays={data.defaultTermDays}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default FirmStatement;
